import {useQuery, useQueryClient} from '@aboutyou/data-fetcher';
import {
  useDefaultCookieDomain,
  useIsStorageConsentGiven,
} from '@aboutyou/storage';
import {doAsync, localStorageRemoveItem} from '@aboutyou/utils';
import {USER_CACHE_KEY, USER_OPTIONAL_CACHE_KEY} from '../../CacheKeys';
import {
  getUserDataFromCookies,
  getUserDataFromLocalStorage,
  setLocalStorageDataWithEncoding,
} from '../../utils/read-write-user-data';
import {sha256Fingerprint} from '../../utils/sha256Fingerprint';
import {BroadcastUserDataToTabs} from '../SyncSessionBetweenTabs';
import {createDataStore} from '../createDataStore';
import {
  TADARIDA_OPTIONAL_USER_PERSIST_KEY,
  TADARIDA_USER_PERSIST_KEY,
} from './constants';
import {OptionalUserState, UserState} from './types';

/**
 * The first name is persisted per-origin in localStorage, whereas the auth token lives in a
 * domain-wide cookie. We persist a SHA-256 hash of the token that owns the first name alongside
 * it, so that after a cross-origin language switch (a hard navigation to another subdomain) we can
 * tell whether the stored name still belongs to the currently authenticated user. Only the hash is
 * stored to avoid duplicating a usable credential outside the auth cookie.
 */
type PersistedOptionalUserState = OptionalUserState & {ownerTokenHash?: string};

/**
 * Only surface the persisted first name when it provably belongs to the currently authenticated
 * user. Otherwise (e.g. a stale name left over from a previous session on this origin, exposed by
 * a hard reload after a cross-origin language switch) return no user so the logo shows nothing and
 * the authoritative customer fetch can repopulate the correct name.
 */
export const reconcilePersistedOptionalUser = (
  persisted: PersistedOptionalUserState | null,
  currentUserTokenHash: string | null,
): OptionalUserState => {
  if (!persisted?.userData) {
    return {userData: null};
  }

  if (
    currentUserTokenHash &&
    persisted.ownerTokenHash === currentUserTokenHash
  ) {
    return {userData: persisted.userData};
  }

  return {userData: null};
};

const useExpensiveOptionalUser = () => {
  const isStorageConsentGiven = useIsStorageConsentGiven();
  const cookieDomain = useDefaultCookieDomain();
  const queryClient = useQueryClient();

  const getCurrentUserToken = (): string | null => {
    const inMemoryUser = queryClient.getQueryData<UserState>(USER_CACHE_KEY);
    if (inMemoryUser?.token) {
      return inMemoryUser.token;
    }

    const userFromCookie = getUserDataFromCookies<UserState>({
      storageKey: TADARIDA_USER_PERSIST_KEY,
      cookieDomain,
    });
    return userFromCookie?.token ?? null;
  };

  const query = useQuery(
    USER_OPTIONAL_CACHE_KEY,
    async (): Promise<OptionalUserState> => {
      const dataFromLocalStorage =
        getUserDataFromLocalStorage<PersistedOptionalUserState>({
          storageKey: TADARIDA_OPTIONAL_USER_PERSIST_KEY,
        });

      if (!dataFromLocalStorage?.userData) {
        return {userData: null};
      }

      const currentUserToken = getCurrentUserToken();
      const currentUserTokenHash = currentUserToken
        ? await sha256Fingerprint(currentUserToken)
        : null;

      return reconcilePersistedOptionalUser(
        dataFromLocalStorage,
        currentUserTokenHash,
      );
    },
  );

  const saveData = (data: OptionalUserState | undefined) => {
    BroadcastUserDataToTabs.broadcastData({
      key: TADARIDA_OPTIONAL_USER_PERSIST_KEY,
      data,
    });

    if (data) {
      query.updateCache(data);
      if (isStorageConsentGiven) {
        doAsync(async () => {
          const currentUserToken = getCurrentUserToken();
          const ownerTokenHash = currentUserToken
            ? await sha256Fingerprint(currentUserToken)
            : null;
          const dataToPersist: PersistedOptionalUserState = ownerTokenHash
            ? {...data, ownerTokenHash}
            : data;
          setLocalStorageDataWithEncoding({
            storageKey: TADARIDA_OPTIONAL_USER_PERSIST_KEY,
            data: dataToPersist,
          });
        });
      }
    }
  };

  return {...query, updateCache: saveData};
};

export const cleanUpOptionalDataState = () =>
  localStorageRemoveItem(TADARIDA_OPTIONAL_USER_PERSIST_KEY);

const {Provider, useData: useOptionalUser} = createDataStore<OptionalUserState>(
  {
    defaultValue: {
      userData: null,
    },
    useQueryHook: useExpensiveOptionalUser,
  },
);

export {Provider as OptionalUserProvider, useOptionalUser};
